import dayjs from 'dayjs'
import React, { createContext, FC } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Api, espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'

import { useAuth } from 'hooks'
import { HandleCalculo, ICalculoContext } from './types'

type Props = {
    children: React.ReactNode
}

export const CalculoContext = createContext<ICalculoContext>(
    {} as ICalculoContext,
)

export const CalculoProvider: FC<Props> = ({ children }) => {
    const [searchParam] = useSearchParams()

    const navigate = useNavigate()
    const { mutateAsync } = Api.calculo.mutations.useCreate()

    const competencia = useStore(state => state.competencia)
    const competenciaEmpresaAtual = useStore(
        state => state.company?.competenciaAtual,
    )
    const funcionarioId = useStore(state => state.employeeId)

    const empresaId = useStore(state => state.companyId)
    const {
        decoded: { categoria },
    } = useAuth()

    const competenciaConsulta = searchParam.get('competence') ?? competencia

    const { data, error } = espelhoPonto.useFetchById(funcionarioId, {
        queryParams: {
            competencia: competenciaConsulta,
            empresaId,
        },
        enabled: empresaId > 0 && funcionarioId > 0,
        retry: false,
        cacheTime: 0,
        onError: async ({ statusCode, message }) => {
            if (
                statusCode === 400 &&
                message === 'Espelho não encontrado' &&
                categoria !== 5
            ) {
                await handleCalculo({
                    view: 'mensal',
                })
            }
        },
    })

    // Sem espelho na competência, o rodapé mostra o saldo da anterior
    const naoEncontrado =
        (error as { message?: string } | null)?.message ===
        'Espelho não encontrado'

    const { data: espelhoAnterior, isLoading: carregandoAnterior } =
        espelhoPonto.useFetchById(funcionarioId, {
            queryParams: {
                competencia: dayjs(competenciaConsulta)
                    .subtract(1, 'month')
                    .format('YYYY-MM'),
                empresaId,
            },
            enabled: naoEncontrado,
            retry: false,
            cacheTime: 0,
        })

    const handleCalculo: HandleCalculo = async arg => {
        const {
            view,
            justificativa,
            isTrusted,
            competencia: competenciaCalcular = competencia,
        } = arg

        if (!competenciaEmpresaAtual || !competenciaCalcular) return
        if (competenciaEmpresaAtual !== competenciaCalcular) {
            if (isTrusted)
                return toast.warning(
                    'A competência deve estar aberta para calcular esse funcionário',
                ) as any
            else return
        }
        if (funcionarioId === -1) return

        await mutateAsync(
            {
                competencia,
                empresaId,
                justificativa,
                ...(view === 'mensal'
                    ? {
                          funcionarioIds: [funcionarioId],
                      }
                    : {}),
            },
            {
                onSuccess: async () => {
                    await espelhoPonto.invalidateQueries()

                    arg.callback?.()
                    if (arg.navigateTo) navigate(arg.navigateTo)
                },
                onError: err => {
                    const message =
                        err?.message ||
                        err?.descricao ||
                        'Ocorreu um erro durante o cálculo e nenhuma mensagem de erro foi retornada.'

                    toast.warning(message)
                },
            },
        )
    }

    const getHorarios = (zIndex: number) => {
        if (data) {
            const espelhoPonto = data.EspelhoPonto[zIndex]

            return {
                horasTrabalhadas: espelhoPonto?.horasTrabalhadas || '00:00',
                faltas: espelhoPonto?.faltas || '00:00',
                horasExtra: espelhoPonto?.horasExtra || '00:00',
                sobreaviso: espelhoPonto?.sobreaviso || '00:00',
            }
        }

        return {
            horasTrabalhadas: '00:00',
            faltas: '00:00',
            horasExtra: '00:00',
            sobreaviso: '00:00',
        }
    }

    return (
        <CalculoContext.Provider
            value={{
                getHorarios,
                handleCalculo,
                espelho: data,
                competencia: competenciaConsulta,
                espelhoNaoEncontrado: naoEncontrado && !carregandoAnterior,
                espelhoAnterior,
            }}
        >
            {children}
        </CalculoContext.Provider>
    )
}
